import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const DEBOUNCE_MS = 300;

function highlight(text, query) {
  if (!query || !text) return text || '';
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'var(--sky-light, #dbeafe)', color: 'var(--sky-dark, #1e40af)', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');

  // Clear search bar on every page navigation
  useEffect(() => { setQuery(''); setOpen(false); }, [location.pathname]);
  const [results, setResults] = useState({ tickets: [], sa: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const canSeeSA = ['sa_initiator', 'sa_approver', 'ict_manager', 'super_admin', 'branch_manager'].includes(user?.role);
  const canSeeTickets = user?.role !== 'sa_initiator' && user?.role !== 'sa_approver';

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults({ tickets: [], sa: [] });
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const calls = [];
      if (canSeeTickets) calls.push(api.get(`/api/tickets?search=${encodeURIComponent(q)}&limit=5`));
      else calls.push(Promise.resolve({ data: { tickets: [] } }));

      if (canSeeSA) calls.push(api.get(`/api/sa?search=${encodeURIComponent(q)}&limit=5`));
      else calls.push(Promise.resolve({ data: { requests: [] } }));

      const [tRes, saRes] = await Promise.all(calls);
      setResults({
        tickets: tRes.data.tickets || [],
        sa: saRes.data.requests || [],
      });
      setOpen(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [canSeeTickets, canSeeSA]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(query), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [query, search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut: / or Ctrl+K to focus search
  useEffect(() => {
    const handler = (e) => {
      const active = document.activeElement;
      const isTyping = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);
      if (isTyping) return; // never steal focus from an active input
      if (e.key === '/' || (e.ctrlKey && e.key === 'k')) {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const go = (path) => {
    setQuery('');
    setOpen(false);
    navigate(path);
  };

  const hasResults = results.tickets.length > 0 || results.sa.length > 0;
  const showDropdown = open && query.trim().length >= 2;

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
      {/* Search Input */}
      <div style={{ position: 'relative' }}>
        <span style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--muted)', fontSize: 14, pointerEvents: 'none'
        }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          placeholder="Search tickets, SA requests… (press /)"
          type="search"
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          name="omiye-helpdesk-search"
          style={{
            width: '100%', padding: '7px 34px 7px 32px',
            border: '1px solid var(--border)', borderRadius: 8,
            fontSize: 13, background: 'var(--surface)',
            color: 'var(--text)', outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
          onFocusCapture={e => e.target.style.borderColor = 'var(--sky)'}
          onBlurCapture={e => e.target.style.borderColor = 'var(--border)'}
        />
        {loading && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--muted)' }}>
            ⏳
          </span>
        )}
        {!loading && query && (
          <span
            onClick={() => { setQuery(''); setOpen(false); }}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--muted)', fontSize: 14 }}>
            ✕
          </span>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 2000, overflow: 'hidden', maxHeight: 420, overflowY: 'auto'
        }}>
          {!hasResults && !loading && (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              No results for "<strong>{query}</strong>"
            </div>
          )}

          {/* Tickets */}
          {results.tickets.length > 0 && (
            <>
              <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--surface)' }}>
                🎫 Tickets
              </div>
              {results.tickets.map(t => (
                <div key={t.id} onClick={() => go(`/tickets/${t.id}`)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--white)'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'Sora', fontSize: 11, fontWeight: 700, color: 'var(--sky-dark)', background: 'var(--sky-light, #dbeafe)', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                      {highlight(t.ticket_number, query)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {highlight(t.subject, query)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.branch}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>·</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.status?.replace('_', ' ')}</span>
                    {t.affected_staff && <>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>·</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{highlight(t.affected_staff, query)}</span>
                    </>}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* SA Requests */}
          {results.sa.length > 0 && (
            <>
              <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--surface)' }}>
                💰 Salary Advance
              </div>
              {results.sa.map(r => (
                <div key={r.id} onClick={() => go(`/sa/${r.id}`)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--white)'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'Sora', fontSize: 11, fontWeight: 700, color: '#0a6640', background: '#dcfce7', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                      {highlight(r.request_number, query)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {highlight(r.account_name, query)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{r.branch}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>·</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{r.status}</span>
                    {r.account_number && <>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>·</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{highlight(r.account_number, query)}</span>
                    </>}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Footer hint */}
          {hasResults && (
            <div style={{ padding: '6px 14px', fontSize: 10, color: 'var(--muted)', background: 'var(--surface)', textAlign: 'right' }}>
              Press Enter to search all · Esc to close
            </div>
          )}
        </div>
      )}
    </div>
  );
}
